import { useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../../api';
import { useBackgroundJobs } from '../jobs/BackgroundJobsContext';

/**
 * Render-redacted-copy confirm dialog.
 *
 * Fire-and-forget (Background Jobs UX): this is a thin confirm — it shows the
 * redaction count, and "Render" submits the `redact_media` job and closes. No
 * per-modal poll loop: progress lives in the BackgroundJobsBanner and a toast
 * fires on completion with "View redacted copy" / "Download" (jobRegistry).
 */
export default function RedactionRenderModal({ caseId, documentId, count, onClose }) {
  const { submitJob } = useBackgroundJobs();
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  async function handleRender() {
    setSubmitting(true);
    setErrorMsg(null);
    try {
      await submitJob(
        'redact_media',
        api.post(`/cases/${caseId}/documents/${documentId}/redactions/render`, {}),
      );
      onClose?.();
    } catch (e) {
      setErrorMsg(e.message || 'Could not start the render.');
      setSubmitting(false);
    }
  }

  return createPortal((
    <div className="np-export-overlay" onClick={submitting ? undefined : onClose}>
      <div className="np-export-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <div className="np-export-head">
          <h3>Render redacted copy</h3>
          <button className="np-icon-btn" onClick={onClose} title="Close" disabled={submitting}>✕</button>
        </div>
        <div className="np-export-body" style={{ display: 'block', padding: 16 }}>
          <div style={{ fontSize: 13 }}>
            Render a redacted copy with {count} redaction{count === 1 ? '' : 's'} applied.
          </div>
          <div className="text-muted" style={{ fontSize: 12, marginTop: 6 }}>
            The original file is never modified. The redacted copy is verified
            (duration + silence checks) before it's saved, then added to this
            case — we'll notify you when it's ready.
          </div>
          {errorMsg && (
            <div className="bulk-popover-error" style={{ marginTop: 10 }}>{errorMsg}</div>
          )}
        </div>
        <div className="np-export-foot">
          <span style={{ flex: 1 }} />
          <button className="btn" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="btn btn-primary" onClick={handleRender} disabled={submitting}>
            {submitting ? 'Starting…' : 'Render'}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}
